import {
  COMPLIANCE_REGISTRY,
  type ComplianceCategory,
  type ComplianceClaimKind,
  type ComplianceRegistryEntry,
} from "@/constants/complianceRegistry";
import type { ComplianceBadgeModel } from "@/domain/compliance/compliance-badge.model";
import { TAG_TYPES } from "@/constants/tagRegistry";
import type { MenuItemTagV2 } from "@/models/menu-item-v2";
import { getPrimaryFoodType, PRIMARY_FOOD_TYPE_CODES } from "@/utils/tag-dietary";

export type { ComplianceBadgeModel } from "@/domain/compliance/compliance-badge.model";

function normalizeCode(tag: MenuItemTagV2): string {
  return String(tag?.tag_code ?? "").trim().toUpperCase();
}

function resolveEntry(code: string, tag: MenuItemTagV2): ComplianceRegistryEntry | null {
  const configured = COMPLIANCE_REGISTRY[code];
  if (configured) return configured;

  const apiName = String(tag?.tag_name ?? "").trim();
  if (!apiName) return null;

  const type = String(tag?.tag_type ?? "").trim();
  const category: ComplianceCategory | null =
    type === TAG_TYPES.ALLERGEN ? "allergen" : type === TAG_TYPES.DIETARY ? "dietary" : null;
  if (!category) return null;

  const ariaLabel =
    category === "allergen" ? `Contains ${apiName.toLowerCase()}` : apiName;

  return {
    category,
    label: apiName,
    ariaLabel,
    variant: category === "allergen" ? "outline" : "subtle",
    claimKind: category === "allergen" ? "contains" : undefined,
  };
}

function toComplianceBadge(tag: MenuItemTagV2): ComplianceBadgeModel | undefined {
  const code = normalizeCode(tag);
  if (!code) return undefined;

  const entry = resolveEntry(code, tag);
  if (!entry) return undefined;

  const claimKind: ComplianceClaimKind | null =
    entry.category === "allergen" ? (entry.claimKind ?? "contains") : null;

  return {
    id: String(tag?.tag_id ?? code),
    code,
    label: entry.label,
    ariaLabel: entry.ariaLabel,
    category: entry.category,
    claimKind,
    icon: entry.icon,
    variant: entry.variant,
    displayOrder: Number(tag?.display_order) || 0,
  };
}

function filterComplianceTags(
  tags: MenuItemTagV2[] | undefined,
  category: ComplianceCategory
): ComplianceBadgeModel[] {
  const typeFilter = category === "allergen" ? TAG_TYPES.ALLERGEN : TAG_TYPES.DIETARY;
  const source = Array.isArray(tags) ? tags : [];

  return source
    .filter((tag) => String(tag?.tag_type ?? "").trim() === typeFilter)
    .sort((a, b) => (Number(a?.display_order) || 0) - (Number(b?.display_order) || 0))
    .map((tag) => toComplianceBadge(tag))
    .filter((badge): badge is ComplianceBadgeModel => badge !== undefined);
}

export function getComplianceAllergenBadges(
  tags: MenuItemTagV2[] | undefined
): ComplianceBadgeModel[] {
  return filterComplianceTags(tags, "allergen");
}

export function getComplianceContainsAllergenBadges(
  tags: MenuItemTagV2[] | undefined
): ComplianceBadgeModel[] {
  return getComplianceAllergenBadges(tags).filter((badge) => badge.claimKind !== "freeFrom");
}

export function getComplianceFreeFromBadges(
  tags: MenuItemTagV2[] | undefined
): ComplianceBadgeModel[] {
  return getComplianceAllergenBadges(tags).filter((badge) => badge.claimKind === "freeFrom");
}

export function getComplianceDietaryBadges(
  tags: MenuItemTagV2[] | undefined
): ComplianceBadgeModel[] {
  return filterComplianceTags(tags, "dietary");
}

/** Secondary dietary / free-from chips — excludes primary food identity (Veg, Vegan, etc.). */
export function getOptionalDietaryChips(
  tags: MenuItemTagV2[] | undefined
): ComplianceBadgeModel[] {
  const secondaryDietary = getComplianceDietaryBadges(tags).filter(
    (badge) => !PRIMARY_FOOD_TYPE_CODES.has(badge.code)
  );
  const freeFrom = getComplianceFreeFromBadges(tags);

  return [...secondaryDietary, ...freeFrom].sort(
    (a, b) => a.displayOrder - b.displayOrder || a.label.localeCompare(b.label)
  );
}

export function hasMenuItemMetadata(tags: MenuItemTagV2[] | undefined): boolean {
  return (
    getPrimaryFoodType(tags) != null ||
    getComplianceContainsAllergenBadges(tags).length > 0 ||
    getOptionalDietaryChips(tags).length > 0
  );
}

export function hasComplianceInformation(tags: MenuItemTagV2[] | undefined): boolean {
  return (
    getComplianceAllergenBadges(tags).length > 0 ||
    getComplianceDietaryBadges(tags).length > 0
  );
}
